import type React from "react";

import { Button } from "@bcc/components/ui/button";
import { Checkbox } from "@bcc/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bcc/components/ui/dialog";
import { Input } from "@bcc/components/ui/input";
import { Label } from "@bcc/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bcc/components/ui/select";
import { Textarea } from "@bcc/components/ui/textarea";
import { useToast } from "@bcc/hooks/use-toast";
import { AlertCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { setManualAddProductModalTestId } from "./manual-add-product-modal.utils";

interface ManualAddProductModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProductAdded: () => void;
  testId?: string;
}

export function ManualAddProductModal({ open, onOpenChange, onProductAdded, testId }: Readonly<ManualAddProductModalProps>) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    nameOfCoffee: "",
    productDescription: "",
    mainFlavorCategory: "",
    secondaryFlavor: "",
    tastingNotes: "",
    isDecaf: null as boolean | null,
    bagSize: "",
    roasterLevel: "",
    coffeeType: "",
    country: "",
    process: "",
    varietal: "",
    elevationFrom: "",
    elevationTo: "",
    certifications: "",
    roasterNotes: "",
    roasterEffectiveDate: "",
    wouldYouRecommend: "",
    grind: "",
    region: "",
    additionalFlavorNotes: "",
    roasterComments: "",
  });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.nameOfCoffee.trim()) {
      newErrors.nameOfCoffee = "Name of coffee is required";
    }
    if (!formData.productDescription.trim()) {
      newErrors.productDescription = "Product description is required";
    }
    if (!formData.mainFlavorCategory) {
      newErrors.mainFlavorCategory = "Main flavor category is required";
    }
    if (!formData.tastingNotes) {
      newErrors.tastingNotes = "Tasting notes are required";
    }
    if (formData.isDecaf === null) {
      newErrors.isDecaf = "Please specify if this coffee is decaf";
    }
    if (!formData.bagSize) newErrors.bagSize = "Bag size is required";
    if (!formData.roasterLevel) {
      newErrors.roasterLevel = "Roaster level is required";
    }
    if (!formData.coffeeType) newErrors.coffeeType = "Coffee type is required";
    if (!formData.country) newErrors.country = "Country is required";
    if (!formData.roasterEffectiveDate) {
      newErrors.roasterEffectiveDate = "Roaster effective date is required";
    }
    if (!formData.grind) newErrors.grind = "Grind is required";
    if (!formData.region) newErrors.region = "Region is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: t("products.manualAdd.toasts.validationError"),
        description: t("products.manualAdd.toasts.fillRequiredFields"),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));

      toast({
        title: t("products.manualAdd.toasts.productAdded"),
        description: t("products.manualAdd.toasts.productAddedDescription", {
          name: formData.nameOfCoffee,
        }),
      });

      onProductAdded();
      onOpenChange(false);

      // Reset form
      setFormData({
        nameOfCoffee: "",
        productDescription: "",
        mainFlavorCategory: "",
        secondaryFlavor: "",
        tastingNotes: "",
        isDecaf: null,
        bagSize: "",
        roasterLevel: "",
        coffeeType: "",
        country: "",
        process: "",
        varietal: "",
        elevationFrom: "",
        elevationTo: "",
        certifications: "",
        roasterNotes: "",
        roasterEffectiveDate: "",
        wouldYouRecommend: "",
        grind: "",
        region: "",
        additionalFlavorNotes: "",
        roasterComments: "",
      });
      setErrors({});
    } catch {
      toast({
        title: t("common.error"),
        description: t("products.manualAdd.toasts.failedToAdd"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent testId={testId ?? setManualAddProductModalTestId("content")} className="sm:max-w-[864px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("products.manualAdd.title")}</DialogTitle>
          <DialogDescription>{t("products.manualAdd.description")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="nameOfCoffee">
              Name of coffee <span className="text-destructive">*</span>
            </Label>
            <Input
              id="nameOfCoffee"
              placeholder="Enter coffee name"
              value={formData.nameOfCoffee}
              onChange={(e) => setFormData({ ...formData, nameOfCoffee: e.target.value })}
              className={errors.nameOfCoffee ? "border-destructive" : ""}
            />
            {errors.nameOfCoffee && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.nameOfCoffee}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="productDescription">
              Product description <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="productDescription"
              placeholder="Enter product description"
              rows={4}
              value={formData.productDescription}
              onChange={(e) => setFormData({ ...formData, productDescription: e.target.value })}
              className={errors.productDescription ? "border-destructive" : ""}
            />
            {errors.productDescription && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.productDescription}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="mainFlavorCategory">
              Main flavor category <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.mainFlavorCategory} onValueChange={(value) => setFormData({ ...formData, mainFlavorCategory: value })}>
              <SelectTrigger className={errors.mainFlavorCategory ? "border-destructive" : ""}>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fruity">Fruity</SelectItem>
                <SelectItem value="nutty">Nutty</SelectItem>
                <SelectItem value="chocolate">Chocolate</SelectItem>
                <SelectItem value="floral">Floral</SelectItem>
                <SelectItem value="spicy">Spicy</SelectItem>
              </SelectContent>
            </Select>
            {errors.mainFlavorCategory && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.mainFlavorCategory}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="secondaryFlavor">Secondary flavor</Label>
            <Select value={formData.secondaryFlavor} onValueChange={(value) => setFormData({ ...formData, secondaryFlavor: value })}>
              <SelectTrigger>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="berry">Berry</SelectItem>
                <SelectItem value="caramel">Caramel</SelectItem>
                <SelectItem value="citrus">Citrus</SelectItem>
                <SelectItem value="vanilla">Vanilla</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tastingNotes">
              Tasting Notes <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.tastingNotes} onValueChange={(value) => setFormData({ ...formData, tastingNotes: value })}>
              <SelectTrigger className={errors.tastingNotes ? "border-destructive" : ""}>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bright">Bright</SelectItem>
                <SelectItem value="smooth">Smooth</SelectItem>
                <SelectItem value="bold">Bold</SelectItem>
                <SelectItem value="balanced">Balanced</SelectItem>
              </SelectContent>
            </Select>
            {errors.tastingNotes && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.tastingNotes}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isDecaf"
                checked={formData.isDecaf === true}
                onCheckedChange={(checked) => setFormData({ ...formData, isDecaf: checked as boolean })}
                className={errors.isDecaf ? "border-destructive" : ""}
              />
              <Label htmlFor="isDecaf" className="cursor-pointer">
                Is this coffee decaf? <span className="text-destructive">*</span>
              </Label>
            </div>
            {errors.isDecaf && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.isDecaf}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="bagSize">
              Bag Size <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.bagSize} onValueChange={(value) => setFormData({ ...formData, bagSize: value })}>
              <SelectTrigger className={errors.bagSize ? "border-destructive" : ""}>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="250g">250g</SelectItem>
                <SelectItem value="500g">500g</SelectItem>
                <SelectItem value="1kg">1kg</SelectItem>
                <SelectItem value="5kg">5kg</SelectItem>
              </SelectContent>
            </Select>
            {errors.bagSize && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.bagSize}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="roasterLevel">
              Roaster Level <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.roasterLevel} onValueChange={(value) => setFormData({ ...formData, roasterLevel: value })}>
              <SelectTrigger className={errors.roasterLevel ? "border-destructive" : ""}>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">Light</SelectItem>
                <SelectItem value="medium-light">Medium-Light</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="medium-dark">Medium-Dark</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
              </SelectContent>
            </Select>
            {errors.roasterLevel && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.roasterLevel}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="coffeeType">
              Coffee Type <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.coffeeType} onValueChange={(value) => setFormData({ ...formData, coffeeType: value })}>
              <SelectTrigger className={errors.coffeeType ? "border-destructive" : ""}>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="arabica">Arabica</SelectItem>
                <SelectItem value="robusta">Robusta</SelectItem>
                <SelectItem value="blend">Blend</SelectItem>
              </SelectContent>
            </Select>
            {errors.coffeeType && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.coffeeType}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="country">
              Country <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.country} onValueChange={(value) => setFormData({ ...formData, country: value })}>
              <SelectTrigger className={errors.country ? "border-destructive" : ""}>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="brazil">Brazil</SelectItem>
                <SelectItem value="colombia">Colombia</SelectItem>
                <SelectItem value="ethiopia">Ethiopia</SelectItem>
                <SelectItem value="kenya">Kenya</SelectItem>
                <SelectItem value="guatemala">Guatemala</SelectItem>
                <SelectItem value="costa-rica">Costa Rica</SelectItem>
              </SelectContent>
            </Select>
            {errors.country && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.country}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="process">Process</Label>
            <Select value={formData.process} onValueChange={(value) => setFormData({ ...formData, process: value })}>
              <SelectTrigger>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="washed">Washed</SelectItem>
                <SelectItem value="natural">Natural</SelectItem>
                <SelectItem value="honey">Honey</SelectItem>
                <SelectItem value="anaerobic">Anaerobic</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="varietal">Varietal</Label>
            <Select value={formData.varietal} onValueChange={(value) => setFormData({ ...formData, varietal: value })}>
              <SelectTrigger>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bourbon">Bourbon</SelectItem>
                <SelectItem value="caturra">Caturra</SelectItem>
                <SelectItem value="typica">Typica</SelectItem>
                <SelectItem value="geisha">Geisha</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="elevationFrom">Elevation from</Label>
              <Input
                id="elevationFrom"
                type="number"
                placeholder="e.g., 1200"
                value={formData.elevationFrom}
                onChange={(e) => setFormData({ ...formData, elevationFrom: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="elevationTo">Elevation to</Label>
              <Input
                id="elevationTo"
                type="number"
                placeholder="e.g., 1800"
                value={formData.elevationTo}
                onChange={(e) => setFormData({ ...formData, elevationTo: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="certifications">Certifications</Label>
            <Select value={formData.certifications} onValueChange={(value) => setFormData({ ...formData, certifications: value })}>
              <SelectTrigger>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="organic">Organic</SelectItem>
                <SelectItem value="fair-trade">Fair Trade</SelectItem>
                <SelectItem value="rainforest">Rainforest Alliance</SelectItem>
                <SelectItem value="bird-friendly">Bird Friendly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="roasterNotes">Roaster notes</Label>
            <Textarea
              id="roasterNotes"
              placeholder="Enter roaster notes"
              rows={4}
              value={formData.roasterNotes}
              onChange={(e) => setFormData({ ...formData, roasterNotes: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="roasterEffectiveDate">
              Roaster effective date <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <Input
                id="roasterEffectiveDate"
                type="date"
                value={formData.roasterEffectiveDate}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    roasterEffectiveDate: e.target.value,
                  })
                }
                className={errors.roasterEffectiveDate ? "border-destructive" : ""}
              />
            </div>
            {errors.roasterEffectiveDate && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.roasterEffectiveDate}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="wouldYouRecommend">Would you recommend</Label>
            <Select value={formData.wouldYouRecommend} onValueChange={(value) => setFormData({ ...formData, wouldYouRecommend: value })}>
              <SelectTrigger>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="maybe">Maybe</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="grind">
              Grind <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.grind} onValueChange={(value) => setFormData({ ...formData, grind: value })}>
              <SelectTrigger className={errors.grind ? "border-destructive" : ""}>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="whole-bean">Whole Bean</SelectItem>
                <SelectItem value="coarse">Coarse</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="fine">Fine</SelectItem>
                <SelectItem value="espresso">Espresso</SelectItem>
              </SelectContent>
            </Select>
            {errors.grind && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.grind}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="region">
              Region <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.region} onValueChange={(value) => setFormData({ ...formData, region: value })}>
              <SelectTrigger className={errors.region ? "border-destructive" : ""}>
                <SelectValue placeholder="--None--" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="south-america">South America</SelectItem>
                <SelectItem value="central-america">Central America</SelectItem>
                <SelectItem value="africa">Africa</SelectItem>
                <SelectItem value="asia-pacific">Asia Pacific</SelectItem>
              </SelectContent>
            </Select>
            {errors.region && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.region}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="additionalFlavorNotes">Additional flavor notes</Label>
            <Textarea
              id="additionalFlavorNotes"
              placeholder="Enter additional flavor notes"
              rows={4}
              value={formData.additionalFlavorNotes}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  additionalFlavorNotes: e.target.value,
                })
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="roasterComments">Roaster Comments</Label>
            <Textarea
              id="roasterComments"
              placeholder="Enter roaster comments"
              rows={4}
              value={formData.roasterComments}
              onChange={(e) => setFormData({ ...formData, roasterComments: e.target.value })}
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t("common.saving") : t("common.save")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
